import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileText, PackageCheck, Trash2, RefreshCw, Edit2, Save, X, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Challans() {
  const [challans, setChallans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [stageFilter, setStageFilter] = useState('all');
  const [partSearch, setPartSearch] = useState('');
  const [receivingId, setReceivingId] = useState(null);
  const [receiveQty, setReceiveQty] = useState('');
  const [receiveDate, setReceiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [editingId, setEditingId] = useState(null);
  const [editQty, setEditQty] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  const fetchChallans = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (stageFilter !== 'all') params.append('stage_to', stageFilter);
      if (partSearch.trim()) params.append('part_id', partSearch.trim());
      const response = await axios.get(`${API}/challans/?${params.toString()}`);
      setChallans(response.data);
      setSelectedIds(new Set());
    } catch (err) {
      console.error(err);
      toast.error('Failed to load challans');
    } finally {
      setLoading(false);
    }
  }, [filter, stageFilter, partSearch]);

  useEffect(() => { fetchChallans(); }, [fetchChallans]);

  const handleReceive = async (challan) => {
    const qty = parseInt(receiveQty);
    if (!qty || qty <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    try {
      const res = await axios.post(`${API}/challans/${challan.id}/receive`, {
        qty,
        date: new Date(receiveDate).toISOString()
      });
      toast.success(res.data.message);
      setReceivingId(null);
      setReceiveQty('');
      fetchChallans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to receive');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this challan and all associated receipts? This will reverse the received inventory.')) return;
    try {
      const res = await axios.delete(`${API}/challans/${id}`);
      toast.success(res.data.message);
      fetchChallans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleEditSave = async (challan) => {
    const qty = parseInt(editQty);
    if (!qty || qty <= 0) { toast.error('Enter a valid quantity'); return; }
    try {
      const res = await axios.put(`${API}/challans/${challan.id}`, { qty });
      toast.success(res.data.message);
      setEditingId(null);
      setEditQty('');
      fetchChallans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllCompleted = () => {
    const completedIds = challans.filter(c => c.status === 'completed').map(c => c.id);
    setSelectedIds(new Set(completedIds));
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) { toast.error('No challans selected'); return; }
    if (!window.confirm(`Delete ${selectedIds.size} selected completed challans?`)) return;
    try {
      const res = await axios.post(`${API}/challans/bulk-delete`, { ids: Array.from(selectedIds) });
      toast.success(res.data.message);
      setSelectedIds(new Set());
      fetchChallans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'partial': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'completed': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="space-y-6" data-testid="challans-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="challans-title">DELIVERY CHALLANS</h1>
          <p className="text-sm text-text-muted mt-2">Auto-generated from stage issues. Receive material against pending challans.</p>
        </div>
        <Button onClick={fetchChallans} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium" data-testid="refresh-challans">
          <RefreshCw className="w-4 h-4 mr-2" />REFRESH
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap" data-testid="challan-filters">
        <div className="flex gap-2">
          {['pending', 'partial', 'completed', 'all'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm border transition-all ${
                filter === f
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-white text-text-muted border-border hover:border-primary/50'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted uppercase tracking-wider font-medium">Stage:</span>
          <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
            className="h-9 px-3 text-xs border border-border rounded-sm bg-white" data-testid="stage-filter">
            <option value="all">All Stages</option>
            <option value="Cutting">Cutting</option>
            <option value="Forging">Forging</option>
            <option value="Machining">Machining</option>
            <option value="HT">HT</option>
            <option value="FG">FG</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-text-muted" />
          <Input value={partSearch} onChange={(e) => setPartSearch(e.target.value)} placeholder="Search by part..."
            className="w-40 h-9 text-xs rounded-sm" data-testid="challan-part-search" />
        </div>
        {isAdmin && selectedIds.size > 0 && (
          <Button onClick={handleBulkDelete} className="bg-red-600 text-white hover:bg-red-700 rounded-sm text-xs h-9" data-testid="bulk-delete-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1" />DELETE {selectedIds.size} SELECTED
          </Button>
        )}
      </div>

      {/* Challans table */}
      <div className="bg-white border border-border shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-text-muted">Loading challans...</div>
        ) : challans.length === 0 ? (
          <div className="p-12 text-center text-text-muted">
            <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            No {filter !== 'all' ? filter : ''} challans found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  {isAdmin && (
                    <th className="h-10 px-2 text-center font-medium text-muted-foreground bg-gray-50/50 w-10">
                      <input type="checkbox" onChange={selectAllCompleted} checked={selectedIds.size > 0 && selectedIds.size === challans.filter(c => c.status === 'completed').length}
                        className="rounded" data-testid="select-all-checkbox" />
                    </th>
                  )}
                  <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Challan No</th>
                  <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Date</th>
                  <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part</th>
                  <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Stage</th>
                  <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Vendor</th>
                  <th className="h-10 px-4 text-right font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Issued</th>
                  <th className="h-10 px-4 text-right font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Received</th>
                  <th className="h-10 px-4 text-right font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Pending</th>
                  <th className="h-10 px-4 text-center font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Status</th>
                  <th className="h-10 px-4 text-center font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                {challans.map((challan, idx) => (
                  <tr key={challan.id} className={`border-b hover:bg-muted/50 transition-colors ${selectedIds.has(challan.id) ? 'bg-blue-50' : ''}`} data-testid={`challan-row-${idx}`}>
                    {isAdmin && (
                      <td className="p-2 text-center">
                        {challan.status === 'completed' && (
                          <input type="checkbox" checked={selectedIds.has(challan.id)} onChange={() => toggleSelect(challan.id)}
                            className="rounded" data-testid={`select-challan-${idx}`} />
                        )}
                      </td>
                    )}
                    <td className="p-4 font-mono text-xs font-bold">{challan.challan_no}</td>
                    <td className="p-4 font-mono text-xs">{new Date(challan.date).toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric'})}</td>
                    <td className="p-4">
                      <div className="text-xs font-mono font-semibold">{challan.part_id}</div>
                      <div className="text-xs text-text-muted">{challan.part_name}</div>
                    </td>
                    <td className="p-4 text-xs">{challan.stage_from} → {challan.stage_to}</td>
                    <td className="p-4 text-xs">{challan.vendor_name || challan.vendor_id || 'Internal'}</td>
                    <td className="p-4 text-right font-mono text-xs">
                      {editingId === challan.id ? (
                        <div className="flex items-center gap-1 justify-end">
                          <Input
                            type="number"
                            value={editQty}
                            onChange={(e) => setEditQty(e.target.value)}
                            className="w-20 h-7 text-xs text-right rounded-none"
                            min={challan.received_qty || 1}
                            autoFocus
                            data-testid={`edit-qty-${idx}`}
                          />
                          <button onClick={() => handleEditSave(challan)} className="p-0.5 hover:bg-green-100 text-green-600 rounded-sm" data-testid={`save-edit-${idx}`}>
                            <Save className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => { setEditingId(null); setEditQty(''); }} className="p-0.5 hover:bg-red-100 text-red-500 rounded-sm" data-testid={`cancel-edit-${idx}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        challan.qty
                      )}
                    </td>
                    <td className="p-4 text-right font-mono text-xs">{challan.received_qty}</td>
                    <td className="p-4 text-right font-mono text-xs font-semibold">
                      <span className={challan.pending_qty > 0 ? 'text-amber-700' : 'text-emerald-600'}>
                        {challan.pending_qty}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getStatusBadge(challan.status)}`}>
                        {challan.status}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      {challan.status !== 'completed' ? (
                        receivingId === challan.id ? (
                          <div className="flex items-center gap-1 justify-center">
                            <Input
                              type="date"
                              value={receiveDate}
                              onChange={(e) => setReceiveDate(e.target.value)}
                              className="w-32 h-7 text-xs rounded-none"
                              data-testid={`receive-date-${idx}`}
                            />
                            <Input
                              type="number"
                              value={receiveQty}
                              onChange={(e) => setReceiveQty(e.target.value)}
                              placeholder={`Max ${challan.pending_qty}`}
                              className="w-20 h-7 text-xs rounded-none"
                              min="1"
                              max={challan.pending_qty}
                              autoFocus
                              data-testid={`receive-qty-${idx}`}
                            />
                            <button
                              onClick={() => handleReceive(challan)}
                              className="p-1 hover:bg-green-100 text-green-600 rounded-sm"
                              data-testid={`confirm-receive-${idx}`}
                            >
                              <PackageCheck className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => { setReceivingId(null); setReceiveQty(''); }}
                              className="p-1 hover:bg-red-100 text-red-500 rounded-sm text-xs font-bold"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setReceivingId(challan.id); setReceiveQty(challan.pending_qty.toString()); setReceiveDate(new Date().toISOString().split('T')[0]); }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm text-xs font-semibold hover:bg-emerald-100 transition-all"
                            data-testid={`receive-btn-${idx}`}
                          >
                            <PackageCheck className="w-3.5 h-3.5" />RECEIVE
                          </button>
                        )
                      ) : (
                        <span className="text-xs text-emerald-600 font-medium">Done</span>
                      )}
                      {isAdmin && challan.status !== 'completed' && (
                        <span className="inline-flex items-center gap-1 ml-2">
                          <button
                            onClick={() => { setEditingId(challan.id); setEditQty(challan.qty.toString()); }}
                            className="p-1 hover:bg-blue-50 text-blue-400 hover:text-blue-600 rounded-sm"
                            data-testid={`edit-challan-${idx}`}
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDelete(challan.id)}
                            className="p-1 hover:bg-red-50 text-red-400 hover:text-red-600 rounded-sm"
                            data-testid={`delete-challan-${idx}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
